import { Injectable } from '@angular/core';

import { BasemapState } from '@connect-services/client/services/basemap.service';

export interface ProjectSessionStorage {
    basemap?: BasemapState;
    isTerrainElevationActive: boolean;
    selectedFilters: string[];
}

@Injectable({
    providedIn: 'root',
})
export class ProjectSessionStorageService {
    public clear(): void {
        sessionStorage.clear();
    }

    public getBasemapAndTerrainElevation(projectId: string): Omit<ProjectSessionStorage, 'selectedFilters'> {
        const current = this.getProjectStorage(projectId);

        return {
            basemap: current?.basemap ?? undefined,
            isTerrainElevationActive: current?.isTerrainElevationActive ?? false,
        };
    }

    public getProjectStorage(projectId: string): ProjectSessionStorage | null {
        const raw = sessionStorage.getItem(this.getKey(projectId));

        if (!raw) {
            return null;
        }

        const parsed: Partial<ProjectSessionStorage> = JSON.parse(raw);

        return {
            basemap: parsed.basemap,
            isTerrainElevationActive: parsed.isTerrainElevationActive ?? false,
            selectedFilters: parsed.selectedFilters ?? [],
        };
    }

    public setBasemap(projectId: string, basemap: BasemapState): void {
        const current = this.getProjectStorage(projectId);
        const next: ProjectSessionStorage = {
            basemap,
            isTerrainElevationActive: current?.isTerrainElevationActive ?? false,
            selectedFilters: current?.selectedFilters ?? [],
        };
        this.setItem(projectId, next);
    }

    public updateSelectedFilter(projectId: string, selectedFilterIds: string[]): void {
        const stored = this.getProjectStorage(projectId);
        const next: ProjectSessionStorage = {
            basemap: stored?.basemap,
            isTerrainElevationActive: stored?.isTerrainElevationActive ?? false,
            selectedFilters: selectedFilterIds,
        };
        this.setItem(projectId, next);
    }

    public updateTerrainElevation(projectId: string, isTerrainElevationActive: boolean): void {
        const stored = this.getProjectStorage(projectId);
        const next: ProjectSessionStorage = {
            basemap: stored?.basemap,
            isTerrainElevationActive,
            selectedFilters: stored?.selectedFilters ?? [],
        };
        this.setItem(projectId, next);
    }

    private getKey(projectId: string): string {
        return `project-${projectId}`;
    }

    private setItem(projectId: string, value: ProjectSessionStorage): void {
        sessionStorage.setItem(this.getKey(projectId), JSON.stringify(value));
    }
}
